import { ENV } from '../config/env.generated.js';

const DEFAULT_BASE_URL = 'https://beta.api.gigma.ru/api';

function appToken() {
  return ENV.GIGMA_TOKEN ?? '';
}

function baseUrl() {
  return ENV.GIGMA_BASE_URL || DEFAULT_BASE_URL;
}

function headers() {
  return {
    'Content-Type': 'application/json',
    Token: appToken()
  };
}

function authHeaders(bearer) {
  return { ...headers(), Authorization: `Bearer ${bearer}` };
}

/**
 * GigmaError — структурированная ошибка от бэкенда Gigma.
 *
 * Поля:
 *   - message: человекочитаемый текст из `body.message` или `body.error` (русский),
 *     либо `'<label>: HTTP <status>'` fallback. Безопасно для прямого вывода в UI.
 *   - code: числовой код причины. См. таблицу в `agent/forms-and-state.md`:
 *       1001–1003 — OrderPricingException (нет товара на складе, оптовые проблемы)
 *       2001–2011 — DiscountValidationException (промокод не найден / истёк / лимит и т.д.)
 *       2099       — public-fallback «промокод не применим» (generic, на precalculate)
 *       null       — у бэка не было поля `code` (auth-эндпоинты, server 5xx и т.п.)
 *   - httpStatus: 4xx/5xx.
 *   - kind: укрупнённая категория, удобно для switch в сторах:
 *       'pricing' (1xxx) | 'discount' (2xxx) | 'auth' (401) | 'server' (всё остальное)
 *   - endpoint: имя метода, который кинул ошибку (для логов).
 *
 * Никогда не содержит Bearer-токен и тело запроса — только body ответа.
 */
export class GigmaError extends Error {
  constructor({ message, code, httpStatus, kind, endpoint }) {
    super(message);
    this.name = 'GigmaError';
    this.code = code ?? null;
    this.httpStatus = httpStatus ?? null;
    this.kind = kind ?? 'server';
    this.endpoint = endpoint ?? null;
  }
}

async function readError(res, endpoint) {
  // Читаем тело как текст один раз, потом пробуем парсить как JSON.
  // Gigma на ряде эндпоинтов отдаёт plain-string (`"Send password before login"`,
  // `"Password TTL"`) — это валидный JSON-string без обёртки в объект.
  // Эту строку нужно сохранить в message целиком, иначе маппер
  // не сможет распознать «истёк код» и покажет «Неверный код».
  let rawText = '';
  try {
    rawText = await res.text();
  } catch {
    // тело пустое или поток уже прочитан
  }
  let parsed = null;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      // не JSON — оставляем rawText как есть
    }
  }

  let message;
  if (typeof parsed === 'string' && parsed) {
    message = parsed;
  } else if (parsed && typeof parsed === 'object') {
    if (typeof parsed.message === 'string' && parsed.message) {
      message = parsed.message;
    } else if (typeof parsed.error === 'string' && parsed.error) {
      message = parsed.error;
    }
  }
  if (!message && rawText && rawText.length <= 300) {
    message = rawText;
  }
  if (!message) {
    message = `${endpoint}: HTTP ${res.status}`;
  }

  const code =
    parsed && typeof parsed === 'object' && typeof parsed.code === 'number' ? parsed.code : null;
  let kind = 'server';
  if (code != null) {
    if (code >= 1000 && code < 2000) kind = 'pricing';
    else if (code >= 2000 && code < 3000) kind = 'discount';
  }
  if (res.status === 401) kind = 'auth';
  return new GigmaError({ message, code, httpStatus: res.status, kind, endpoint });
}

// Retry на network-level ошибках (CORS-блок от мигающего CDN, обрыв TCP,
// DNS-флэп). НЕ ретраит HTTP 4xx/5xx — у них в теле есть код ошибки от
// бэка (1xxx / 2xxx), который нужно показать пользователю как есть.
// Сценарий, который чиним: api.gigma.ru изредка возвращает 5xx без
// `Access-Control-Allow-Origin`; браузер блокирует ответ, fetch отдаёт
// TypeError('Failed to fetch'). Второй запрос обычно проходит.
//
// ВАЖНО — ретраим ТОЛЬКО идемпотентные вызовы (по умолчанию GET).
// На POST/PUT (createOrder, sendPassword, requestPhoneChange…) ретрай
// НЕЛЬЗЯ: у Gigma нет Idempotency-Key (см. agent/forms-and-state.md:179),
// и CORS-блок может случиться ПОСЛЕ того, как сервер уже принял запрос
// и записал заказ / отправил SMS — второй запрос создаст дубль.
// Phab call-site может явно разрешить ретрай для read-only POST
// (precalculate) через `idempotent: true`.
const NETWORK_RETRY_MAX = 2;
const NETWORK_RETRY_DELAYS = [300, 700];

// Таймаут на одну попытку. Если Gigma висит — режем через AbortController,
// чтобы не залипал `isSubmitting=true` на десятки секунд.
const FETCH_TIMEOUT_MS = 15000;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// ±20% jitter, чтобы при одновременном CDN-сбое у нескольких клиентов
// ретраи не били синхронно в одну точку.
function jitter(ms) {
  const spread = ms * 0.2;
  return Math.round(ms - spread + Math.random() * spread * 2);
}

async function doFetchWithTimeout(url, init) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function fetchWithRetry(url, init, { idempotent }) {
  // Не-идемпотентные запросы — один shot. CORS-флап после успешной
  // записи на бэке должен подняться к caller'у как ошибка, а не
  // молча создать дубль.
  if (!idempotent) {
    return doFetchWithTimeout(url, init);
  }
  let lastErr;
  for (let attempt = 0; attempt < NETWORK_RETRY_MAX; attempt++) {
    try {
      return await doFetchWithTimeout(url, init);
    } catch (err) {
      // AbortError (от user-инициированной отмены или нашего таймаута)
      // — пробрасываем сразу, не ретраим. Иначе таймауты складываются.
      if (err && err.name === 'AbortError') throw err;
      // На сетевых сбоях fetch резолвится TypeError'ом ('Failed to fetch'
      // при CORS/DNS/обрыве TCP). На HTTP-ответ (даже 5xx) промис fetch
      // РЕЗОЛВИТСЯ, поэтому catch не глотает осмысленную ошибку бэка.
      lastErr = err;
      if (attempt < NETWORK_RETRY_MAX - 1) {
        await sleep(jitter(NETWORK_RETRY_DELAYS[attempt]));
        continue;
      }
    }
  }
  throw lastErr;
}

async function callGigma({ endpoint, method, path, bearer, body, idempotent }) {
  const init = {
    method,
    headers: bearer ? authHeaders(bearer) : headers()
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  // По умолчанию идемпотентны только GET. Явный `idempotent: true` нужен
  // для read-only POST вроде /orders/precalculate.
  const isIdempotent = idempotent === true || method === 'GET';
  const res = await fetchWithRetry(`${baseUrl()}/${path}`, init, { idempotent: isIdempotent });
  if (!res.ok) throw await readError(res, endpoint);
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export function normalizePhone(raw) {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.startsWith('8') && digits.length === 11) return '7' + digits.slice(1);
  if (digits.startsWith('7') && digits.length === 11) return digits;
  if (digits.length === 10) return '7' + digits;
  return digits;
}

export async function sendPassword(login) {
  await callGigma({
    endpoint: 'sendPassword',
    method: 'POST',
    path: 'counterparty/send_password',
    body: { phone: login }
  });
}

export async function login(identifier, password) {
  const data = await callGigma({
    endpoint: 'login',
    method: 'POST',
    path: 'counterparty/login',
    body: { phone: identifier, password }
  });
  const token = data?.counterparty?.access_token?.value;
  if (!token) {
    throw new GigmaError({
      message: 'Токен не получен от сервера',
      code: null,
      httpStatus: 200,
      kind: 'server',
      endpoint: 'login'
    });
  }
  return token;
}

export async function updateCounterparty(bearer, data) {
  await callGigma({
    endpoint: 'updateCounterparty',
    method: 'PUT',
    path: 'counterparty',
    bearer,
    body: data
  });
}

export async function getDeliveryTypes() {
  return callGigma({ endpoint: 'getDeliveryTypes', method: 'GET', path: 'counterparty/delivery_types' });
}

export async function getPaymentTypes() {
  return callGigma({ endpoint: 'getPaymentTypes', method: 'GET', path: 'counterparty/payment_types' });
}

export async function getShops() {
  return callGigma({ endpoint: 'getShops', method: 'GET', path: 'counterparty/shops' });
}

function envInt(key) {
  const raw = ENV[key];
  if (raw === undefined || raw === null || raw === '') return undefined;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

export async function precalculateOrder(productId, promoCode, quantity = 1) {
  const qty = Math.max(1, Math.floor(Number(quantity) || 1));
  const body = { products: [{ id: productId, quantity: qty }] };
  if (promoCode) body.promo_code = promoCode;
  return callGigma({
    endpoint: 'precalculate',
    method: 'POST',
    path: 'counterparty/orders/precalculate',
    body,
    // Read-only POST: считает скидку без побочных эффектов, безопасно ретраить.
    idempotent: true
  });
}

export async function createOrder(bearer, productId, promoCode = null, quantity = 1) {
  const deliveryIdFromEnv = envInt('GIGMA_DELIVERY_TYPE_ID');
  const paymentIdFromEnv = envInt('GIGMA_PAYMENT_TYPE_ID');
  const shopIdFromEnv = envInt('GIGMA_SHOP_ID');

  let deliveryTypeId = deliveryIdFromEnv;
  let paymentTypeId = paymentIdFromEnv;
  let needsShop = false;
  let resolvedShopId = shopIdFromEnv;

  if (!deliveryTypeId || !paymentTypeId) {
    const [deliveryRes, paymentRes] = await Promise.all([getDeliveryTypes(), getPaymentTypes()]);

    if (!deliveryTypeId) {
      const deliveryType =
        deliveryRes.deliveryType.find((d) => d.is_active && d.price === '0.00') ??
        deliveryRes.deliveryType.find((d) => d.is_active) ??
        deliveryRes.deliveryType[0];
      if (!deliveryType) {
        throw new GigmaError({
          message: 'Нет доступных способов доставки',
          code: null,
          httpStatus: null,
          kind: 'server',
          endpoint: 'createOrder'
        });
      }
      deliveryTypeId = deliveryType.id;
      needsShop =
        deliveryType.name.toLowerCase().includes('самовывоз') ||
        deliveryType.name.toLowerCase().includes('pickup');
    }

    if (!paymentTypeId) {
      const paymentType =
        paymentRes.paymentTypes.find((p) => p.name.toLowerCase().includes('онлайн')) ??
        paymentRes.paymentTypes[0];
      if (!paymentType) {
        throw new GigmaError({
          message: 'Нет доступных способов оплаты',
          code: null,
          httpStatus: null,
          kind: 'server',
          endpoint: 'createOrder'
        });
      }
      paymentTypeId = paymentType.id;
    }
  }

  if (needsShop && resolvedShopId === undefined) {
    const shopsRes = await getShops();
    resolvedShopId = shopsRes.shops[0]?.id;
    if (!resolvedShopId) {
      throw new GigmaError({
        message: 'Нет доступных магазинов для самовывоза',
        code: null,
        httpStatus: null,
        kind: 'server',
        endpoint: 'createOrder'
      });
    }
  }

  const qty = Math.max(1, Math.floor(Number(quantity) || 1));
  const body = {
    delivery_type_id: deliveryTypeId,
    payment_type_id: paymentTypeId,
    products: [{ id: productId, quantity: qty }]
  };
  if (resolvedShopId !== undefined) body.shop_id = resolvedShopId;
  if (promoCode) body.promo_code = promoCode;

  const data = await callGigma({
    endpoint: 'createOrder',
    method: 'POST',
    path: 'counterparty/orders',
    bearer,
    body
  });
  // Возвращаем и paymentLink, и orderId. orderId нужен в AccountPage:
  // при возврате с YooKassa (?paid=1) точно знаем, какой заказ ждёт
  // подтверждения, без эвристики «самый свежий pending».
  return {
    paymentLink: data?.order?.payment_link ?? null,
    orderId: data?.order?.id ?? null
  };
}

export async function getAuthorizedCounterparty(bearer) {
  return callGigma({
    endpoint: 'getAuthorizedCounterparty',
    method: 'GET',
    path: 'counterparty',
    bearer
  });
}

export async function getOrders(bearer) {
  return callGigma({ endpoint: 'getOrders', method: 'GET', path: 'counterparty/orders', bearer });
}

export async function logoutCounterparty(bearer, fromAllDevices = false) {
  await callGigma({
    endpoint: 'logout',
    method: 'POST',
    path: 'counterparty/logout',
    bearer,
    body: { from_all_devices: !!fromAllDevices }
  });
}

export async function requestPhoneChange(bearer, newPhone) {
  await callGigma({
    endpoint: 'requestPhoneChange',
    method: 'POST',
    path: 'counterparty/phone/request_change',
    bearer,
    body: { new_phone_number: newPhone }
  });
}

export async function verifyPhoneChange(bearer, code) {
  return callGigma({
    endpoint: 'verifyPhoneChange',
    method: 'POST',
    path: 'counterparty/phone/verify_change',
    bearer,
    body: { password: code }
  });
}
